/**
 * Proof-of-work for draft generation. Same rule as the server: find the smallest `solution` for
 * which the first `bits` bits of `sha256("{nonce}:{solution}")` are zero.
 *
 * `crypto.subtle.digest` is async per call and too slow for tens of thousands of tries, so this
 * keeps a small synchronous SHA-256 (about 2^16 tries on average, under a second on phones).
 * A Web Worker solves it while the customer is still picking chips (ported from duri).
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const W = new Uint32Array(64);

/** SHA-256 of an ASCII string (hex nonce, decimal solution) as eight 32-bit words. */
export function sha256Words(message: string): Uint32Array {
  const len = message.length;
  const blocks = Math.ceil((len + 9) / 64);
  const bytes = new Uint8Array(blocks * 64);
  for (let i = 0; i < len; i++) bytes[i] = message.charCodeAt(i) & 0xff;
  bytes[len] = 0x80;
  const bitLen = len * 8;
  const view = new DataView(bytes.buffer);
  view.setUint32(bytes.length - 4, bitLen >>> 0);
  view.setUint32(bytes.length - 8, Math.floor(bitLen / 0x100000000));

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  for (let b = 0; b < blocks; b++) {
    for (let t = 0; t < 16; t++) W[t] = view.getUint32(b * 64 + t * 4);
    for (let t = 16; t < 64; t++) {
      const x = W[t - 15];
      const y = W[t - 2];
      const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
      const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
      W[t] = (W[t - 16] + s0 + W[t - 7] + s1) | 0;
    }
    let a = h0;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    let bb = h1;
    for (let t = 0; t < 64; t++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[t] + W[t]) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & bb) ^ (a & c) ^ (bb & c);
      const t2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = bb;
      bb = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + bb) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }
  return new Uint32Array([h0, h1, h2, h3, h4, h5, h6, h7]);
}

/**
 * Same check as the server (api/services/pow.py `solution_ok`):
 * `int.from_bytes(sha256(f"{nonce}:{solution}"), "big") >> (256 - bits) == 0`, i.e. the first
 * `bits` bits of the digest are zero. Works for any 0 to 256 bits, word by word.
 */
export function solutionOk(nonce: string, solution: number, bits: number): boolean {
  if (bits <= 0) return true;
  const words = sha256Words(`${nonce}:${solution}`);
  let left = Math.min(bits, 256);
  for (let i = 0; left > 0; i++, left -= 32) {
    const w = (words[i] as number) >>> 0;
    if (left >= 32) {
      if (w !== 0) return false;
    } else if (w >>> (32 - left) !== 0) {
      return false;
    }
  }
  return true;
}

export function solve(nonce: string, bits: number): number {
  for (let n = 0; ; n++) {
    if (solutionOk(nonce, n, bits)) return n;
  }
}
